import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  authMethod,
  AttributeSensitivity,
  formatData,
  formatDatasetPath,
  handleError,
  Role,
  Asset,
  resolveGlossaryAssetSubmit,
} from '../helpers';
import {
  Changelog,
  DataTable,
  MainColumn,
  MorphButton,
  ObjectDetails,
  SkeletonLoader,
  Tabs,
} from '../components';
import {
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  useNotFound,
  useMessage,
} from '../hooks';
import {
  entityService,
  datasetService,
  datasetGroupService,
  datasourceService,
  attributeService,
  glossaryService,
  systemService,
  userService,
  userGroupService,
  userGroupMemberService,
} from '../services';

export const User = () => {
  const { queryParams, resetParams } = useQueryParams();

  const [user, setUser] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [userGroupsAll, setUserGroupsAll] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [ownedItems, setOwnedItems] = useState([]);
  const [ownedItemsCount, setOwnedItemsCount] = useState({});
  const [ownedItemsTotalCount, setOwnedItemsTotalCount] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingUserGroups, setLoadingUserGroups] = useState(true);
  const [defaultSelectedObject, setDefaultSelectedObject] = useState({ tab: queryParams.object });
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  let { user_id } = useParams();
  if (user_id === undefined) {
    user_id = loggedInUser.user_id;
  }

  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { notFound, setNotFound, NotFound } = useNotFound();

  useEffect(() => {
    fetchItems();
    fetchOwnedItemsCount();
    if (loggedInUser && loggedInUser.user_role_name === Role.admin) {
      fetchUserGroups();
      fetchAllUserGroups();
    }
  }, [user_id]);

  const fetchItems = async () => {
    userService
      .getById(user_id, true)
      .then(res => {
        if (res.users.length < 1) {
          setNotFound(true);
        } else {
          setUser(res.users[0]);
          setLoading(false);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      });
  };

  const fetchOwnedItemsCount = async () => {
    userService
      .getOwnedItemsCount(user_id)
      .then(res => {
        let tmp = {};
        let defaultObject;

        const objectSortOrder = [
          'attribute',
          'entity',
          'glossary',
          'dataset',
          'dataset_group',
          'datasource',
          'system',
        ];
        res.owned_items_count.sort(
          (a, b) => objectSortOrder.indexOf(a.object_name) - objectSortOrder.indexOf(b.object_name)
        );

        res.owned_items_count.forEach(result => {
          if (result.owned_items_count > 0 && defaultObject === undefined) {
            defaultObject = result.object_name;
          }
          tmp = { ...tmp, [result.object_name]: parseInt(result.owned_items_count) };
        });

        defaultObject = res.owned_items_count.find(x => x.object_name === queryParams.object)
          ? queryParams.object
          : defaultObject;

        //defaultObject = queryParams.object || defaultObject

        setDefaultSelectedObject({ tab: defaultObject });
        fetchOwnedItems(defaultObject);
        setOwnedItemsCount(tmp);

        setOwnedItemsTotalCount(
          (tmp.attribute || 0) +
            (tmp.dataset || 0) +
            (tmp.dataset_group || 0) +
            (tmp.datasource || 0) +
            (tmp.system || 0) +
            (tmp.entity || 0) +
            (tmp.glossary || 0)
        );
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchOwnedItems = async objectName => {
    if (objectName) {
      // objectName is undefined if user does not own any items
      setLoadingTable(true);
      userService
        .getOwnedItems(user_id, objectName)
        .then(res => {
          let result = res.owned_items;

          if (objectName === Asset.Attribute) {
            // The PII icon column renders a label and its tooltip rather than the raw flag.
            result = result.map(x => ({
              ...x,
              pii: x.is_pii ? 'PII' : null,
              pii_tooltip: x.is_pii
                ? 'Classified as Personally Identifiable Information (PII)'
                : null,
            }));
          }

          if (objectName === Asset.Dataset) {
            // Render each owned dataset as its full endpoint path (API datasets); the ancestors
            // are attached to the row by the owned-items endpoint.
            result = result.map(x => ({
              ...x,
              dataset_path: formatDatasetPath(x, { ancestors: x.ancestors }),
            }));
          }

          setOwnedItems(result);
          setLoadingTable(false);
        })
        .catch(err => {
          showMessage(err, 'error');
        });
    } else {
      setLoadingTable(false); // Needed to show "no result" message
    }
  };

  const fetchUserGroups = async () => {
    userGroupService
      .getByUserId(user_id)
      .then(res => {
        setUserGroups(res.user_groups);
        setLoadingUserGroups(false);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAllUserGroups = async () => {
    userGroupService
      .getAll()
      .then(res => {
        setUserGroupsAll(res.user_groups);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editUser = async data => {
    const users = { users: Array.isArray(data) ? data : [data] };

    await userService
      .update(users)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editUserGroup = async data => {
    const userGroups = { user_groups: Array.isArray(data) ? data : [data] };

    await userGroupService
      .update(userGroups)
      .then(async res => {
        fetchUserGroups();
        showMessage(res.message);
        clearSelectedItems();
        hideModalDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  const editDataset = async data => {
    const items = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editDatasetGroup = async data => {
    const items = { dataset_groups: Array.isArray(data) ? data : [data] };

    await datasetGroupService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editDatasource = async data => {
    const items = { datasources: Array.isArray(data) ? data : [data] };

    await datasourceService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editSystem = async data => {
    const items = { systems: Array.isArray(data) ? data : [data] };

    await systemService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editGlossary = async data => {
    const items = { glossaries: Array.isArray(data) ? data : [data] };

    await glossaryService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Saves an edited glossary asset. One handler serves both kinds: the form's role picker can move an
   * asset between Entity and Attribute, so which resource to call is decided from the submitted row
   * rather than from the button that opened the dialog.
   */
  const editGlossaryAsset = async data => {
    const { service, payload, isCreate } = resolveGlossaryAssetSubmit(data, 'edit');

    await (isCreate ? service.create(payload) : service.update(payload))
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteEntity = async data => {
    await entityService
      .destroy({ entities: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDataset = async data => {
    await datasetService
      .destroy({ datasets: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDatasetGroup = async data => {
    await datasetGroupService
      .destroy({ dataset_groups: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDatasource = async data => {
    await datasourceService
      .destroy({ datasources: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteAttribute = async data => {
    await attributeService
      .destroy({ attributes: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteGlossary = async data => {
    await glossaryService
      .destroy({ glossaries: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteSystem = async data => {
    await systemService
      .destroy({ systems: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteUser = async data => {
    await userService
      .destroy({ users: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/users`);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const updateMember = async event => {
    let { value } = '';

    value = event.value;
    const userId = parseInt(user_id);

    const existingUser = userGroups.find(x => x.user_group_id === value);
    const user_group_members = [
      {
        user_id: userId,
        user_group_id: value,
        user_group_member_id: existingUser?.user_group_member_id,
      },
    ];

    if (existingUser) {
      await removeMember(user_group_members);
    } else {
      await addMember(user_group_members);
    }
  };
  const addMember = async data => {
    await userGroupMemberService
      .create({ user_group_members: data })
      .then(async res => {
        fetchUserGroups();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const removeMember = async data => {
    await userGroupMemberService
      .destroy({ user_group_members: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchUserGroups();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const requestResults = userGroupsAll.map(result => {
    return {
      value: result.user_group_id,
      label: result.user_group_name,
      tooltip: result.user_group_name,
    };
  });

  const onObjectChange = object => {
    resetParams({ object: object });
    ownedItems.length > 0 && fetchOwnedItems(object);
    setDefaultSelectedObject({ tab: object });
    clearSelectedItems();
  };

  const onTabChange = tab => {
    clearSelectedItems();
    resetParams({ tab: tab, object: undefined });
    !userGroups && fetchUserGroups();
    tab === 'owned_assets' && fetchOwnedItems(defaultSelectedObject.tab);
  };

  const data = loading ? [] : user;

  const editableAdmin = loggedInUser && loggedInUser.user_role_name === Role.admin;
  const editable = editableAdmin;

  const userGroupTypeValues = Object.entries(authMethod).reduce(
    (a, [key, value]) => ({ ...a, [key]: { label: value } }),
    {}
  );

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="USER"
          title={data.user_fullname}
          secondaryTitle={`${data.user_title ? data.user_title : ''}${data.user_department ? '\n' + data.user_department : ''}`}
          subtitleText={data && !loading && data.user_username}
          details={[
            editable && { title: 'Source Id', data: data.user_source_id },
            editable && {
              title: data?.user_auth_method === 'local' ? 'Created' : 'Added',
              data: formatData(data.created_timestamp, 'datetime'),
            },
            { title: 'Id', data: data.user_id, tooltip: 'Katalogue-internal id for this asset' },
          ]}
          showDescription={false}
          photo={data.user_photo}
          loading={loading}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editUser', [data], editUser),
            },
            loggedInUser &&
              data.user_id !== loggedInUser.user_id && {
                value: 'Delete',
                onClick: () => showModalDialog('deleteUser', [data], deleteUser),
              },
          ]}
        >
          <h3>{!loading ? 'Email' : <SkeletonLoader width="5vw" />}</h3>
          <div className="display-linebreak">
            {!loading ? (
              data.user_email === null ? (
                '-'
              ) : (
                <a className="link" href={'mailto:' + data.user_email}>
                  {data.user_email}
                </a>
              )
            ) : (
              <SkeletonLoader width="10vw" />
            )}
          </div>
          {editable && (
            <React.Fragment>
              <h3>{!loading ? 'Role' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? (
                  data.user_role_name === null ? (
                    '-'
                  ) : (
                    data.user_role_name
                  )
                ) : (
                  <SkeletonLoader width="10vw" />
                )}
              </div>

              <h3>{!loading ? 'User Type' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? (
                  data.user_auth_method === null ? (
                    '-'
                  ) : (
                    authMethod[data.user_auth_method]
                  )
                ) : (
                  <SkeletonLoader width="10vw" />
                )}
              </div>

              <h3>{!loading ? 'Status' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? (
                  !data.user_is_enabled ? (
                    <span className="pill pill-semifull-red">Disabled</span>
                  ) : (
                    <span className="pill">Enabled</span>
                  )
                ) : (
                  <SkeletonLoader width="10vw" />
                )}
              </div>

              <h3>{!loading ? 'Last Login' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? (
                  data.user_last_login_timestamp === null ? (
                    <span className="no-result-text">Never logged in</span>
                  ) : (
                    formatData(data.user_last_login_timestamp, 'datetime')
                  )
                ) : (
                  <SkeletonLoader width="10vw" />
                )}
              </div>
            </React.Fragment>
          )}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          disableTabsWithoutResults={false}
          showNoResultsMessage={true}
          onTabChange={onTabChange}
          defaultSelectedTab={defaultSelectedTab}
        >
          <div label="Assigned Assets" tabid="owned_assets" resultCount={ownedItemsTotalCount}>
            {ownedItemsTotalCount > 0 ? (
              <Tabs
                className="buttons left"
                onTabChange={onObjectChange}
                showResultCount={false}
                disableTabsWithoutResults={true}
                defaultSelectedTab={defaultSelectedObject}
              >
                <div
                  label="Attributes"
                  tabid={Asset.Attribute}
                  resultCount={(ownedItemsCount && ownedItemsCount.attribute) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'attribute_role_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'attribute_role_name',
                      },
                      {
                        id: 'attribute_name',
                        name: 'Name',
                        link: '/attributes/:attribute_id',
                        tooltip: 'attribute_description',
                        className: 'bold',
                      },
                      {
                        id: 'attribute_unit_code',
                        name: 'Attribute Unit',
                        tooltip: 'attribute_unit_name',
                      },
                      {
                        id: 'attribute_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        // An attribute belongs to a glossary, and the same name can be used in another one.
                        id: 'glossary_name',
                        name: 'Glossary',
                        link: '/glossaries/:glossary_id',
                      },
                      {
                        id: 'pii',
                        name: '',
                        type: 'icon',
                        tooltip: 'pii_tooltip',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'attribute_sensitivity_name',
                        name: 'Data Sensitivity',
                        type: 'pill',
                        value: 'attribute_sensitivity_level',
                        values: AttributeSensitivity,
                        tooltip: 'attribute_sensitivity_name',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                      {
                        id: 'assignment_type',
                        name: 'Assignment Type',
                        tooltip: 'assigned_as_description',
                        className: 'small-text',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editAttribute',
                            items || selectedItems,
                            editGlossaryAsset
                          ),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteAttribute',
                            items || selectedItems,
                            deleteAttribute
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="attribute_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'attribute_name',
                      'attribute_description',
                      'attribute_role_name',
                      'attribute_unit_name',
                      'glossary_name',
                      'attribute_sensitivity_name',
                      'is_pii',
                      'assigned_as',
                      'assignment_type',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Entities"
                  tabid={Asset.Entity}
                  resultCount={(ownedItemsCount && ownedItemsCount.entity) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'entity_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id/:entity_id',
                        tooltip: 'entity_description',
                        className: 'bold',
                      },
                      {
                        id: 'entity_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'glossary_name', name: 'Glossary', link: '/glossaries/:glossary_id' },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                      {
                        id: 'assignment_type',
                        name: 'Assignment Type',
                        tooltip: 'assigned_as_description',
                        className: 'small-text',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editEntity', items || selectedItems, editGlossaryAsset),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteEntity', items || selectedItems, deleteEntity),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="entity_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'entity_name',
                      'entity_description',
                      'glossary_name',
                      'assigned_as',
                      'assignment_type',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Glossaries"
                  tabid={Asset.Glossary}
                  resultCount={(ownedItemsCount && ownedItemsCount.glossary) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'glossary_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id',
                        tooltip: 'glossary_description',
                        className: 'bold',
                      },
                      { id: 'glossary_type', name: 'Type', className: 'small-text' },
                      {
                        id: 'glossary_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                      {
                        id: 'assignment_type',
                        name: 'Assignment Type',
                        tooltip: 'assigned_as_description',
                        className: 'small-text',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editGlossary', items || selectedItems, editGlossary),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteGlossary', items || selectedItems, deleteGlossary),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="glossary_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'glossary_name',
                      'glossary_type',
                      'glossary_description',
                      'assigned_as',
                      'assignment_type',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasets"
                  tabid={Asset.Dataset}
                  resultCount={(ownedItemsCount && ownedItemsCount.dataset) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'dataset_type_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'dataset_type_name',
                      },
                      {
                        // dataset_path holds the formatDatasetPath() output (its segments link
                        // themselves); sort still keys off dataset_name.
                        id: 'dataset_path',
                        name: 'Name',
                        sortField: 'dataset_name',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Dataset Group',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                      },
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                        className: 'no-right-margin',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                      {
                        id: 'assignment_type',
                        name: 'Assignment Type',
                        tooltip: 'assigned_as_description',
                        className: 'small-text',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDataset', items || selectedItems, editDataset),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteDataset', items || selectedItems, deleteDataset),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="dataset_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'dataset_name',
                      'dataset_description',
                      'dataset_type_name',
                      'dataset_group_name',
                      'datasource_name',
                      'system_name',
                      'assigned_as',
                      'assignment_type',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Dataset Groups"
                  tabid={Asset.DatasetGroup}
                  resultCount={(ownedItemsCount && ownedItemsCount.dataset_group) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'dataset_group_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                        tooltip: 'dataset_group_description',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                      {
                        id: 'assignment_type',
                        name: 'Assignment Type',
                        tooltip: 'assigned_as_description',
                        className: 'small-text',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editDatasetGroup',
                            items || selectedItems,
                            editDatasetGroup
                          ),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteDatasetGroup',
                            items || selectedItems,
                            deleteDatasetGroup
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="dataset_group_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'dataset_group_name',
                      'dataset_description',
                      'datasource_name',
                      'system_name',
                      'assigned_as',
                      'assignment_type',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasources"
                  tabid={Asset.Datasource}
                  resultCount={(ownedItemsCount && ownedItemsCount.datasource) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id',
                        tooltip: 'datasource_description',
                        className: 'bold',
                      },
                      {
                        id: 'datasource_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'system_name', name: 'System', link: '/systems/:system_id' },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                      {
                        id: 'assignment_type',
                        name: 'Assignment Type',
                        tooltip: 'assigned_as_description',
                        className: 'small-text',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDatasource', items || selectedItems, editDatasource),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteDatasource',
                            items || selectedItems,
                            deleteDatasource
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="datasource_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'datasource_name',
                      'datasource_description',
                      'datasource_type_category',
                      'datasource_type_name',
                      'system_name',
                      'assigned_as',
                      'assignment_type',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Systems"
                  tabid={Asset.System}
                  resultCount={(ownedItemsCount && ownedItemsCount.system) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'system_name',
                        name: 'Name',
                        link: '/systems/:system_id',
                        tooltip: 'system_description',
                        className: 'bold',
                      },
                      { id: 'system_code', name: 'Code' },
                      { id: 'system_type', name: 'Type', className: 'small-text' },
                      {
                        id: 'system_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                      {
                        id: 'assignment_type',
                        name: 'Assignment Type',
                        tooltip: 'assigned_as_description',
                        className: 'small-text',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editSystem', items || selectedItems, editSystem),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteSystem', items || selectedItems, deleteSystem),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="system_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'system_name',
                      'system_type',
                      'system_description',
                      'assigned_as',
                      'assignment_type',
                    ]}
                    showViewModes={true}
                  />
                </div>
              </Tabs>
            ) : (
              !loadingTable && <div className="no-result-text">No assigned assets</div>
            )}
          </div>
          {editableAdmin && (
            <div
              label="User Groups"
              tabid="user_groups"
              resultCount={loadingUserGroups ? undefined : userGroups.length}
            >
              <DataTable
                columns={[
                  {
                    id: 'user_group_name',
                    name: '',
                    type: 'owner',
                    link: '/user_groups/:user_group_id',
                    className: 'bold',
                    data: { photo: 'user_group_photo' },
                  },
                  {
                    id: 'user_group_name',
                    name: 'Name',
                    link: '/user_groups/:user_group_id',
                    className: 'bold',
                  },
                  { id: 'user_group_default_role_name', name: 'Default Role' },
                  {
                    id: 'user_group_type',
                    name: 'Type',
                    value: 'user_group_type',
                    values: userGroupTypeValues,
                    className: 'small-text',
                  },
                ]}
                buttons={[
                  {
                    customButton: (
                      <MorphButton
                        name="user_id"
                        value={userGroups.map(x => ({
                          value: x.user_group_id,
                          label: x.user_group_name,
                        }))}
                        options={requestResults}
                        onChange={updateMember}
                        placeholder="Search User Groups..."
                        onToggle={clearSelectedItems}
                      />
                    ),
                    action: 'add',
                    onClick: () => {},
                  },
                  {
                    label: 'Edit',
                    action: 'edit',
                    onClick: items =>
                      showSideDialog('editUserGroup', items || selectedItems, editUserGroup),
                  },
                  {
                    label: 'Remove',
                    action: 'remove',
                    tooltip:
                      'Remove user from this user group. The User itself will not be deleted.',
                    onClick: items =>
                      showModalDialog('removeUserGroup', items || selectedItems, removeMember),
                  },
                ]}
                data={userGroups}
                editable={editable}
                idColumn="user_group_id"
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                loading={loadingUserGroups}
                filterable={true}
                filterObjects={['user_group_name', 'user_group_default_role_name']}
              />
            </div>
          )}
          {editableAdmin && (
            <div label="Changelog" tabid="changelog">
              <Changelog
                objectName={Asset.User}
                objectId={user_id}
                filterObjects={[
                  'changedData.new_data||old_data.user_fullname',
                  'changedData.new_data||old_data.user_username',
                  'changedData.new_data||old_data.user_email',
                  'changedData.new_data||old_data.user_role_name',
                  'changedData.property',
                  'changed_by_user_username',
                  'is_service_account_user',
                ]}
                filterObjectsValueMap={{
                  is_service_account_user: {
                    true: { label: 'Datasource Sync' },
                    false: { label: '' },
                  },
                }}
              />
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
};
